if (!customElements.get('door-builder')) {
  class DoorBuilder extends HTMLElement {
    constructor() {
      super();
      // Map<catId, Map<variantId, { qty, title, price, priceFormatted, image, imageAlt }>>
      this._selections = new Map();
    }

    connectedCallback() {
      this._bindEvents();
    }

    _bindEvents() {
      // Category toggle buttons
      this.querySelectorAll('[data-cat-toggle]').forEach(btn => {
        btn.addEventListener('click', e => {
          const cat = e.target.closest('[data-cat-id]');
          if (cat) this._toggleCategory(cat);
        });
      });

      // "Show all N options" — the rows past the first fifty, which Liquid
      // could not hand us on the product page.
      this.querySelectorAll('[data-show-more]').forEach(btn => {
        btn.addEventListener('click', () => this._loadMore(btn));
      });

      this._bindRowEvents(this);
      this._bindCtaEvents();
      this._bindMainFormIntercept();
    }

    // The page has two ways to buy: the theme's own product form near the top,
    // and the builder's CTA down here. Whichever one the customer presses, the
    // add-ons they picked have to travel with the door — so when anything is
    // selected we take the theme form's submit over and route it through the
    // same bundled /cart/add call.
    _bindMainFormIntercept() {
      // Capture on document, and match the form at event time rather than
      // holding a reference: product-form.js listens on the form itself, so an
      // ancestor in the capture phase is the only way to be sure we run first
      // and can stop it — and the variant picker re-renders the section, which
      // would leave any stored reference pointing at a detached form.
      if (this._formInterceptBound) return;
      this._formInterceptBound = true;

      // The click is the reliable hook. Intercepting only 'submit' depends on
      // the form element still being the one we expect after the variant
      // picker re-renders the section, and on nothing else calling
      // preventDefault first; the click on the button happens before any of
      // that. Submit is kept as well so pressing Enter in the form is covered.
      document.addEventListener(
        'click',
        (e) => {
          if (!this.isConnected) return;
          const button = e.target.closest?.('[type="submit"], button[name="add"]');
          if (!button) return;
          const form = button.form || button.closest('form');
          if (!form || !this._isMainForm(form) || !this._hasSelections()) return;

          e.preventDefault();
          e.stopImmediatePropagation();
          this._submitBundle(form, button);
        },
        true
      );

      document.addEventListener(
        'submit',
        (e) => {
          // A re-rendered section leaves the old builder detached but its
          // listener still on document — it must not answer for the new one.
          if (!this.isConnected) return;
          const form = e.target;
          if (!(form instanceof HTMLFormElement)) return;
          if (!this._isMainForm(form)) return;
          if (!this._hasSelections()) return;

          e.preventDefault();
          e.stopImmediatePropagation();
          this._submitBundle(form, form.querySelector('[type="submit"]'));
        },
        true
      );
    }

    /** Takes the door's variant and quantity from the theme's own form. */
    _submitBundle(form, button) {
      const data = new FormData(form);
      this._addToCart({
        variantId: parseInt(data.get('id'), 10),
        quantity: Math.max(1, parseInt(data.get('quantity'), 10) || 1),
        button
      });
    }

    /**
     * Is this the theme's add-to-cart form for the door itself? Liquid hands us
     * the form's id, which is the reliable answer; the section fallback covers
     * the builder being rendered somewhere that could not pass it.
     */
    _isMainForm(form) {
      if (form.dataset.type !== 'add-to-cart-form') return false;

      // getAttribute, not .id: a form exposes its named controls as properties,
      // and this form has <input name="id"> for the variant — so form.id is
      // that input element, and comparing it to a string is always false.
      if (this.dataset.mainForm) return form.getAttribute('id') === this.dataset.mainForm;

      // Card and quick-add forms are add-to-cart forms too, and they belong to
      // other products — only the one sitting in our own section counts.
      if (form.closest('.card, quick-add-modal, .card-wrapper')) return false;
      const section = this.closest('.shopify-section') || document.body;
      return section.contains(form);
    }

    _hasSelections() {
      return this._getTotalSelectionsCount() > 0;
    }

    // Fetches the add-ons past the first fifty, a page at a time, from
    // templates/collection.door-builder-options.liquid. Failure is quiet on
    // purpose: the first fifty are already on the page and usable, so a dead
    // network should cost the customer the button, not the builder.
    async _loadMore(btn) {
      if (btn.dataset.loading === 'true') return;
      const handle = btn.dataset.collection;
      const page = parseInt(btn.dataset.nextPage, 10) || 2;
      if (!handle) return;

      btn.dataset.loading = 'true';
      const label = btn.textContent;
      btn.textContent = 'Loading…';

      try {
        const url = `${window.Shopify?.routes?.root || '/'}collections/${handle}?view=door-builder-options&page=${page}`;
        const res = await fetch(url.replace('//collections', '/collections'));
        if (!res.ok) throw new Error(`${res.status}`);
        const html = await res.text();

        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const rows = parsed.querySelectorAll('.door-builder__product-row');
        if (!rows.length) throw new Error('no rows');

        const container = btn.closest('.door-builder__product-list')?.querySelector('[data-rows]');
        if (!container) throw new Error('nowhere to put them');

        const fragment = document.createDocumentFragment();
        rows.forEach(row => fragment.appendChild(row));
        const appended = Array.from(fragment.children);
        container.appendChild(fragment);
        appended.forEach(row => this._bindRowEvents(row));

        // The template marks whether another page exists, so the button knows
        // when it has nothing left to offer.
        if (parsed.querySelector('[data-has-next]')) {
          btn.dataset.nextPage = String(page + 1);
          btn.textContent = label;
          btn.dataset.loading = 'false';
        } else {
          btn.remove();
        }
      } catch (err) {
        console.warn('[door-builder] could not load more options:', err.message);
        btn.textContent = label;
        btn.dataset.loading = 'false';
      }
    }

    // Row-level handlers, kept separate from _bindEvents so the rows fetched by
    // _loadMore can be wired up the same way after they are appended.
    _bindRowEvents(root) {
      // SELECT + buttons in product list
      root.querySelectorAll('[data-select-btn]').forEach(btn => {
        btn.addEventListener('click', e => {
          const row = e.target.closest('[data-variant-id]');
          const cat = row.closest('[data-cat-id]');
          if (row && cat) this._selectItem(cat, row);
        });
      });

      // Deselect buttons in product list
      root.querySelectorAll('[data-deselect-btn]').forEach(btn => {
        btn.addEventListener('click', e => {
          const row = e.target.closest('[data-variant-id]');
          const cat = row.closest('[data-cat-id]');
          if (row && cat) this._deselectItem(cat, row);
        });
      });

      // Qty minus/plus in product list rows
      root.querySelectorAll('[data-qty-minus]').forEach(btn => {
        btn.addEventListener('click', e => {
          const row = e.target.closest('[data-variant-id]');
          const cat = row.closest('[data-cat-id]');
          const input = row.querySelector('.door-builder__mini-qty-input');
          if (row && cat && input) {
            const newVal = Math.max(1, parseInt(input.value, 10) - 1);
            input.value = newVal;
            this._updateSelectionQty(cat.dataset.catId, row.dataset.variantId, newVal);
          }
        });
      });

      root.querySelectorAll('[data-qty-plus]').forEach(btn => {
        btn.addEventListener('click', e => {
          const row = e.target.closest('[data-variant-id]');
          const cat = row.closest('[data-cat-id]');
          const input = row.querySelector('.door-builder__mini-qty-input');
          if (row && cat && input) {
            const newVal = Math.min(99, parseInt(input.value, 10) + 1);
            input.value = newVal;
            this._updateSelectionQty(cat.dataset.catId, row.dataset.variantId, newVal);
          }
        });
      });

    }

    _bindCtaEvents() {
      // The only add-to-cart button is the theme's own, up in the product form,
      // and its quantity input drives the door line — so the combined total has
      // to follow that input rather than a stepper of our own.
      const qtyInput = this._mainQtyInput();
      if (qtyInput) {
        qtyInput.addEventListener('change', () => this._updateCTA());
        qtyInput.addEventListener('input', () => this._updateCTA());
      }
    }

    /** The theme product form's quantity input for the door. */
    _mainQtyInput() {
      const formId = this.dataset.mainForm;
      if (formId) {
        const form = document.getElementById(formId);
        const scoped =
          form?.querySelector('input[name="quantity"]') ||
          document.querySelector(`input[name="quantity"][form="${formId}"]`);
        if (scoped) return scoped;
      }
      return document.querySelector('form[data-type="add-to-cart-form"] input[name="quantity"]');
    }

    _toggleCategory(catEl) {
      const catId = catEl.dataset.catId;
      const toggleBtn = catEl.querySelector('[data-cat-toggle]');
      const productList = catEl.querySelector('.door-builder__product-list');
      const summary = catEl.querySelector('.door-builder__selected-summary');

      const isExpanded = !productList.hidden;

      if (isExpanded) {
        // Collapse
        productList.hidden = true;

        this._rebuildSummary(catEl, catId);
        const selCount = this._selections.get(catId)?.size || 0;
        summary.hidden = selCount === 0;

        if (selCount > 0) {
          this._setToggleState(toggleBtn, false, `${selCount} selected`);
        } else {
          this._setToggleState(toggleBtn, false);
        }
      } else {
        // Expand
        productList.hidden = false;
        this._setToggleState(toggleBtn, true);
        summary.hidden = true;
      }
    }

    _setToggleState(btn, open, labelOverride) {
      const label = btn.querySelector('[data-toggle-label]');
      const icon = btn.querySelector('.door-builder__toggle-icon');
      if (open) {
        btn.classList.replace('door-builder__toggle-btn--select', 'door-builder__toggle-btn--close');
        if (label) label.textContent = 'CLOSE';
        if (icon) icon.textContent = '×';
      } else {
        btn.classList.replace('door-builder__toggle-btn--close', 'door-builder__toggle-btn--select');
        if (label) label.textContent = labelOverride || 'SELECT';
        if (icon) icon.textContent = labelOverride ? '' : '+';
      }
    }

    _selectItem(catEl, rowEl) {
      const catId = catEl.dataset.catId;
      const variantId = rowEl.dataset.variantId;

      if (!this._selections.has(catId)) {
        this._selections.set(catId, new Map());
      }

      const catMap = this._selections.get(catId);
      if (!catMap.has(variantId)) {
        catMap.set(variantId, {
          qty: 1,
          title: rowEl.dataset.title,
          price: parseInt(rowEl.dataset.price, 10),
          priceFormatted: rowEl.dataset.priceFormatted,
          image: rowEl.dataset.image,
          imageAlt: rowEl.dataset.imageAlt || ''
        });
      }

      // Update select btn to selected state, show qty stepper
      const selectBtn = rowEl.querySelector('[data-select-btn]');
      const qtyWrap = rowEl.querySelector('.door-builder__row-qty-wrap');
      if (selectBtn) { selectBtn.innerHTML = 'SELECTED'; selectBtn.disabled = true; selectBtn.classList.add('is-selected'); }
      if (qtyWrap) qtyWrap.hidden = false;

      // Sync qty input with stored value
      const input = rowEl.querySelector('.door-builder__mini-qty-input');
      if (input) input.value = catMap.get(variantId).qty;

      this._updateCTA();

      // Auto-close the category
      const productList = catEl.querySelector('.door-builder__product-list');
      if (productList && !productList.hidden) this._toggleCategory(catEl);
    }

    _deselectItem(catEl, rowEl) {
      const catId = catEl.dataset.catId;
      const variantId = rowEl.dataset.variantId;

      const catMap = this._selections.get(catId);
      if (catMap) catMap.delete(variantId);

      // Restore select btn, hide qty stepper
      const selectBtn = rowEl.querySelector('[data-select-btn]');
      const qtyWrap = rowEl.querySelector('.door-builder__row-qty-wrap');
      if (selectBtn) { selectBtn.innerHTML = 'SELECT <span>+</span>'; selectBtn.disabled = false; selectBtn.classList.remove('is-selected'); }
      if (qtyWrap) qtyWrap.hidden = true;

      // Reset qty input
      const input = rowEl.querySelector('.door-builder__mini-qty-input');
      if (input) input.value = 1;

      this._updateCTA();
    }

    _updateSelectionQty(catId, variantId, qty) {
      const catMap = this._selections.get(catId);
      if (catMap && catMap.has(variantId)) {
        catMap.get(variantId).qty = qty;
      }
      this._updateCTA();
    }

    _rebuildSummary(catEl, catId) {
      const summary = catEl.querySelector('.door-builder__selected-summary');
      summary.innerHTML = '';

      const catMap = this._selections.get(catId);
      if (!catMap || catMap.size === 0) return;

      catMap.forEach((item, variantId) => {
        const card = document.createElement('div');
        card.className = 'door-builder__sel-card';
        card.dataset.variantId = variantId;

        card.innerHTML = `
          <div class="door-builder__sel-card-left">
            <div class="door-builder__sel-thumb">
              ${item.image ? `<img src="${item.image}" alt="${item.imageAlt}" width="40" height="40" loading="lazy">` : ''}
            </div>
            <div class="door-builder__sel-info">
              <strong class="door-builder__sel-title">${item.title}</strong>
              <span class="door-builder__sel-price">${item.priceFormatted}</span>
            </div>
          </div>
          <div class="door-builder__mini-qty">
            <button class="door-builder__mini-qty-btn" type="button" data-sel-minus>−</button>
            <input class="door-builder__mini-qty-input" type="number" value="${item.qty}" min="1" max="99" readonly>
            <button class="door-builder__mini-qty-btn" type="button" data-sel-plus>+</button>
          </div>
          <button class="door-builder__change-btn" type="button" data-cat-toggle>CHANGE</button>
        `;

        // Wire up sel-card qty buttons
        card.querySelector('[data-sel-minus]').addEventListener('click', () => {
          const input = card.querySelector('.door-builder__mini-qty-input');
          const newVal = Math.max(1, parseInt(input.value, 10) - 1);
          input.value = newVal;
          this._updateSelectionQty(catId, variantId, newVal);
          // Also update in the product list row if visible
          this._syncRowQty(catEl, variantId, newVal);
        });

        card.querySelector('[data-sel-plus]').addEventListener('click', () => {
          const input = card.querySelector('.door-builder__mini-qty-input');
          const newVal = Math.min(99, parseInt(input.value, 10) + 1);
          input.value = newVal;
          this._updateSelectionQty(catId, variantId, newVal);
          this._syncRowQty(catEl, variantId, newVal);
        });

        // Wire up CHANGE button
        card.querySelector('[data-cat-toggle]').addEventListener('click', () => {
          this._toggleCategory(catEl);
        });

        summary.appendChild(card);
      });
    }

    _syncRowQty(catEl, variantId, qty) {
      const row = catEl.querySelector(`.door-builder__product-row[data-variant-id="${variantId}"]`);
      if (row) {
        const input = row.querySelector('.door-builder__mini-qty-input');
        if (input) input.value = qty;
      }
    }

    _updateCTA() {
      const cta = this.querySelector('.door-builder__cta');
      const totalEl = this.querySelector('[data-addons-total]');
      const combinedEl = this.querySelector('[data-combined-total]');

      let total = 0;
      let hasAny = false;

      this._selections.forEach(catMap => {
        catMap.forEach(item => {
          total += item.price * item.qty;
          hasAny = true;
        });
      });

      if (hasAny) {
        cta.hidden = false;
        if (totalEl) totalEl.textContent = this._money(total);
        // The door itself plus the add-ons — what actually lands in the cart.
        if (combinedEl) combinedEl.textContent = this._money(total + this._mainLineTotal());
      } else {
        cta.hidden = true;
      }
    }

    /** Price of the door on its own, at the quantity currently selected. */
    _mainLineTotal() {
      const price = parseInt(this.dataset.mainProductPrice, 10);
      if (isNaN(price)) return 0;
      const input = this._mainQtyInput();
      const qty = Math.max(1, parseInt(input?.value, 10) || 1);
      return price * qty;
    }

    _money(pennies) {
      return (pennies / 100).toLocaleString('en-GB', { style: 'currency', currency: 'GBP' });
    }

    _getTotalSelectionsCount() {
      let count = 0;
      this._selections.forEach(catMap => { count += catMap.size; });
      return count;
    }

    // opts lets the theme's product form reuse this: it passes its own variant,
    // quantity and submit button. With no opts we fall back to that same form —
    // its button is the page's only add to cart.
    async _addToCart(opts = {}) {
      const btn = opts.button || document.querySelector(`form[id="${this.dataset.mainForm}"] [type="submit"]`);
      if (!btn) return;

      // One add at a time. Two /cart/add calls racing while the browser has no
      // cart cookie yet make the server mint a cart for each, and the losing
      // Set-Cookie takes its lines with it — the cart the customer ends up
      // holding is missing half the bundle.
      if (this._adding) return;
      this._adding = true;

      const mainQtyInput = this._mainQtyInput();
      const mainVariantId = opts.variantId || parseInt(this.dataset.variantId, 10);
      const mainQty = opts.quantity || (mainQtyInput ? parseInt(mainQtyInput.value, 10) : 1);

      if (!mainVariantId) {
        console.error('DoorBuilder: No main variant ID found');
        this._adding = false;
        return;
      }

      // Build bundle ID
      const bundleId = 'awi-' + Date.now();

      // Build items array
      const items = [
        {
          id: mainVariantId,
          quantity: mainQty,
          properties: { '_bundle_id': bundleId }
        }
      ];

      this._selections.forEach(catMap => {
        catMap.forEach((item, variantId) => {
          items.push({
            id: parseInt(variantId, 10),
            quantity: item.qty,
            properties: {
              '_bundle_id': bundleId,
              '_is_addon': '1'
            }
          });
        });
      });

      // Loading state — the theme's own button also drives a spinner and
      // aria-disabled, so match what product-form.js would have done to it.
      btn.classList.add('loading');
      btn.disabled = true;
      btn.setAttribute('aria-disabled', 'true');
      const spinner = btn.querySelector('.loading__spinner');
      if (spinner) spinner.classList.remove('hidden');

      try {
        const cartDrawer = document.querySelector('cart-drawer');

        // Ask for the drawer sections in the same request, the way every other
        // add-to-cart path in this theme does. Without them the response is just
        // the added lines, so the drawer had nothing to repaint from and kept the
        // markup it rendered with at page load — an empty cart stayed empty.
        const payload = { items };
        if (cartDrawer) {
          payload.sections = cartDrawer.getSectionsToRender().map((section) => section.id);
          payload.sections_url = window.location.pathname;
        }

        // routes.cart_add_url, the same endpoint product-form.js posts to. It
        // is already locale-prefixed by Liquid, and it is the one known to
        // return the rendered sections — /cart/add.js does not always, which
        // left the drawer opening on its page-load markup.
        const addUrl = window.routes?.cart_add_url || '/cart/add';

        const response = await fetch(addUrl, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify(payload)
        });

        if (!response.ok) {
          const err = await response.json();
          throw new Error(err.description || err.message || 'Could not add to cart');
        }

        const data = await response.json();

        if (cartDrawer && data.sections) {
          // Repaints #CartDrawer and the icon bubble from the server's own
          // rendering, then opens the drawer itself — so no separate open()
          // call, and deliberately no cartUpdate publish: that would send the
          // drawer off on a second, racing fetch to rebuild the same markup.
          cartDrawer.renderContents(data);
        } else if (cartDrawer) {
          // No sections came back, so fetch them. Opening the drawer without
          // repainting shows the markup it was rendered with at page load —
          // the new lines only appear on reload, which is not a cart the
          // customer can trust.
          await this._refreshCartDrawer(cartDrawer);
          // Something was just added, so the drawer is not empty whatever class
          // it was rendered with — and is-empty hides .cart__contents outright.
          cartDrawer.classList.remove('is-empty');
          cartDrawer.querySelector('.drawer__inner')?.classList.remove('is-empty');
          cartDrawer.open();
        } else {
          try {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'door-builder', cartData: data });
          } catch (e) {
            document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
          }
        }

        // Reset selections
        this._selections.clear();
        this._resetUI();

      } catch (err) {
        console.error('DoorBuilder: Add to cart failed', err);
        // Show error to user
        const errorEl = this.querySelector('.door-builder__error') || (() => {
          const el = document.createElement('p');
          el.className = 'door-builder__error';
          el.style.cssText = 'color:red;font-size:1.4rem;margin-top:1rem;';
          this.querySelector('.door-builder__cta').appendChild(el);
          return el;
        })();
        errorEl.textContent = err.message || 'Sorry, could not add to cart. Please try again.';
        setTimeout(() => { errorEl.textContent = ''; }, 5000);
      } finally {
        btn.classList.remove('loading');
        btn.disabled = false;
        btn.removeAttribute('aria-disabled');
        if (spinner) spinner.classList.add('hidden');
        this._adding = false;
      }
    }

    /** Rebuilds the drawer and the icon bubble from the server, in place. */
    async _refreshCartDrawer(cartDrawer) {
      const base = window.routes?.cart_url || `${window.Shopify?.routes?.root || '/'}cart`;
      const sections = cartDrawer.getSectionsToRender();

      await Promise.all(
        sections.map(async (section) => {
          const target = section.selector
            ? document.querySelector(section.selector)
            : document.getElementById(section.id);
          if (!target) return;

          const res = await fetch(`${base}?section_id=${section.id}`, { credentials: 'same-origin' });
          if (!res.ok) return;
          const html = await res.text();
          const parsed = new DOMParser().parseFromString(html, 'text/html');
          const source = parsed.querySelector(section.selector || '.shopify-section');
          if (source) target.innerHTML = source.innerHTML;
        })
      );
    }

    _resetUI() {
      // Hide CTA
      const cta = this.querySelector('.door-builder__cta');
      if (cta) cta.hidden = true;

      // Reset all categories
      this.querySelectorAll('[data-cat-id]').forEach(catEl => {
        const toggleBtn = catEl.querySelector('[data-cat-toggle]');
        const productList = catEl.querySelector('.door-builder__product-list');
        const summary = catEl.querySelector('.door-builder__selected-summary');

        if (toggleBtn) this._setToggleState(toggleBtn, false);
        productList.hidden = true;
        if (summary) { summary.hidden = true; summary.innerHTML = ''; }

        // Reset all product rows
        catEl.querySelectorAll('.door-builder__product-row').forEach(row => {
          const selectBtn = row.querySelector('[data-select-btn]');
          const qtyWrap = row.querySelector('.door-builder__row-qty-wrap');
          const input = row.querySelector('.door-builder__mini-qty-input');
          if (selectBtn) { selectBtn.innerHTML = 'SELECT <span>+</span>'; selectBtn.disabled = false; selectBtn.classList.remove('is-selected'); }
          if (qtyWrap) qtyWrap.hidden = true;
          if (input) input.value = 1;
        });
      });

    }
  }

  customElements.define('door-builder', DoorBuilder);
}
